
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import AmountDisplayerFilterable from '@/components/reports-v3/components/filterables/AmountDisplayerFilterable.vue';
import moment from 'moment';
import { COL, getTableName, sqlSum, sqlSumByDocType, TIMEZONE_OFFSET_MS } from '@/worker/db/sqlHelpers';
import FilterWidget from '../shared/FilterWidget.vue';
import {PermissionsGroup} from '@/store/models.def';
import { addComma, roundToFixed } from '@/util/number';
import gdbx from '@/store/modules/gdbx';
import IncludeCreditNoteToggle from '@/components/IncludeCreditNoteToggle.vue';
import IncludeDebitNoteToggle from '@/components/IncludeDebitNoteToggle.vue';
import SlPhModuleOnlyToggle from '@/components/SlPhModuleOnlyToggle.vue';
import settingx from '@/store/modules/settingx';

@Component({
	components: {
		AmountDisplayerFilterable,
		IncludeCreditNoteToggle,
		IncludeDebitNoteToggle,
		SlPhModuleOnlyToggle,
	},
})
export default class CustomerCumulativeSalesNumberDisplayer extends FilterWidget {
	@Prop({ default: '' }) public defaultCustomer!: string;

	public filterIds: Array<
		| 'date'
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
	> = [];

	public relationship = '';
	public allTimeSales = '' || 0;
	public averageAnnualSales = '' || 0;
	public servingYear = '' || 0;
	public accountManager = '';

	@Watch('accountManager', { immediate: true })
	public onAccountManagerChange(newVal) {
		this.$emit('returnAccountManager', this.accountManager);
	}

	@Watch('relationship', { immediate: true })
	public onRelationshipChange(newVal) {
		this.$emit('returnRelationship', this.relationship);
	}

	public get permissionIds(): PermissionsGroup[] {
		return ['customers', 'sales'];
	}

	public get currentYear() {
		return moment(this.selectedAsOfDate).year();
	}

	public get currency() {
		return gdbx.currencySymbol;
	}

	public get decimal() {
		return gdbx.numDecimal;
	}

	public get averageAnnualSalesText() {
		return addComma(this.averageAnnualSales, this.decimal);
	}

	public get includeCreditNote() {
		return settingx.now.includeCreditNote;
	}

	public get includeDebitNote() {
		return settingx.now.includeDebitNote;
	}

	public get slPhModuleOnly() {
		return settingx.now.slPhModuleOnly;
	}

	public get expensiveHook() {
		const {
			selectedDateRange,
			selectedAsOfDate,
			selectedAgents,
			selectedStockItems,
			currentYear,
			defaultCustomer,
			includeCreditNote,
			includeDebitNote,
			slPhModuleOnly,
		} = this;
		return JSON.stringify([
			selectedDateRange,
			selectedAsOfDate,
			selectedAgents,
			selectedStockItems,
			currentYear,
			defaultCustomer,
			includeCreditNote,
			includeDebitNote,
			slPhModuleOnly,
		]);
	}

	public async expensiveCalc() {
		const jobId = this.currentJobId;
		this.generateLoadingText(0.2, 0.3);

		const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
		const customerFilter = this.defaultCustomer
			? ` AND "CODE" = '${escapeStr(this.defaultCustomer)}'`
			: '';

		const globalTable = getTableName('globalCustomers');
		const salesTable = getTableName('sales');
		const cnTable = getTableName('creditNotes');
		const arTable = getTableName('customerTrades');
		const lastAsOfDate = moment(this.selectedAsOfDate).add(-1, 'year').endOf('year').valueOf();

		const queryHead = await this._ensureTablesLoaded([
			'globalCustomers', 'sales', 'creditNotes', 'customerTrades',
		] as any[]);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.3, 0.5);

		// Get globalCustomers info for relationship and account manager
		const globalRows = await queryHead.rawQuery(`
			SELECT "AGENT" as agent, TRY_CAST("FIRSTDEAL" AS BIGINT) as first_deal
			FROM ${globalTable}
			WHERE "CODE" = '${escapeStr(this.defaultCustomer)}'
		`);

		if (globalRows.length > 0) {
			this.accountManager = gdbx.agentNames[globalRows[0].agent] || '';
			const firstDeal = Number(globalRows[0].first_deal) || 0;
			if (firstDeal) {
				const firstDealMmt = moment(firstDeal);
				const yearDiff = moment(this.selectedAsOfDate).diff(firstDealMmt, 'years', true);
				const roundedYearDiff = roundToFixed(yearDiff, 2);

				if (roundedYearDiff !== 0 && roundedYearDiff <= 1) {
					const monthDiff = Math.trunc(roundedYearDiff * 12);
					this.relationship = monthDiff + ' ' + 'month(s)';
				} else if (roundedYearDiff > 1) {
					const yearOnly = Math.trunc(yearDiff);
					const monthOnly = roundedYearDiff - yearOnly;
					const monthDiff = Math.trunc(monthOnly * 12);
					this.relationship = yearOnly + ' ' + 'year(s)' + ' ' + monthDiff + ' ' + 'month(s)';
				} else {
					this.relationship = 'New';
				}
			}
		} else {
			this.accountManager = '';
		}

		this.checkInterrupted(jobId);
		this.generateLoadingText(0.5, 0.9);

		// Run all queries in parallel
		const [slResult, slCnResult, lastSlResult, lastSlCnResult, arResult, lastArResult] = await Promise.all([
			queryHead.rawQuery(`
				SELECT
					${sqlSumByDocType(['IV', 'CS'], COL.AMOUNT, 'iv_cs_amount')},
					${sqlSumByDocType(['DN'], COL.AMOUNT, 'dn_amount')}
				FROM ${salesTable}
				WHERE TRY_CAST(${COL.DOCDATE} AS BIGINT) <= ${this.selectedAsOfDate}
					${customerFilter}
			`),
			queryHead.rawQuery(`
				SELECT ${sqlSum(COL.AMOUNT, 'cn_amount')}
				FROM ${cnTable}
				WHERE TRY_CAST(${COL.DOCDATE} AS BIGINT) <= ${this.selectedAsOfDate}
					${customerFilter}
			`),
			queryHead.rawQuery(`
				SELECT
					${sqlSumByDocType(['IV', 'CS'], COL.AMOUNT, 'iv_cs_amount')},
					${sqlSumByDocType(['DN'], COL.AMOUNT, 'dn_amount')},
					COUNT(DISTINCT EXTRACT(YEAR FROM EPOCH_MS(TRY_CAST(${COL.DOCDATE} AS BIGINT) + ${TIMEZONE_OFFSET_MS}))) as year_count
				FROM ${salesTable}
				WHERE TRY_CAST(${COL.DOCDATE} AS BIGINT) <= ${lastAsOfDate}
					${customerFilter}
			`),
			queryHead.rawQuery(`
				SELECT ${sqlSum(COL.AMOUNT, 'cn_amount')}
				FROM ${cnTable}
				WHERE TRY_CAST(${COL.DOCDATE} AS BIGINT) <= ${lastAsOfDate}
					${customerFilter}
			`),
			queryHead.rawQuery(`
				SELECT
					${sqlSumByDocType(['IV'], COL.LOCALAMOUNT, 'iv_amount')},
					${sqlSumByDocType(['CN'], COL.LOCALAMOUNT, 'cn_amount')},
					${sqlSumByDocType(['DN'], COL.LOCALAMOUNT, 'dn_amount')}
				FROM ${arTable}
				WHERE TRY_CAST(${COL.DOCDATE} AS BIGINT) <= ${this.selectedAsOfDate}
					${customerFilter}
			`),
			queryHead.rawQuery(`
				SELECT
					${sqlSumByDocType(['IV'], COL.LOCALAMOUNT, 'iv_amount')},
					${sqlSumByDocType(['CN'], COL.LOCALAMOUNT, 'cn_amount')},
					${sqlSumByDocType(['DN'], COL.LOCALAMOUNT, 'dn_amount')},
					COUNT(DISTINCT EXTRACT(YEAR FROM EPOCH_MS(TRY_CAST(${COL.DOCDATE} AS BIGINT) + ${TIMEZONE_OFFSET_MS}))) as year_count
				FROM ${arTable}
				WHERE TRY_CAST(${COL.DOCDATE} AS BIGINT) <= ${lastAsOfDate}
					${customerFilter}
			`),
		]);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.95);

		const slRow = slResult[0] || {};
		const slIvCsAmount = Number(slRow.iv_cs_amount) || 0;
		const slDnAmount = Number(slRow.dn_amount) || 0;

		const slCnRow = slCnResult[0] || {};
		const slCnAmount = Number(slCnRow.cn_amount) || 0;

		const lastSlRow = lastSlResult[0] || {};
		const lastSlIvCsAmount = Number(lastSlRow.iv_cs_amount) || 0;
		const lastSlDnAmount = Number(lastSlRow.dn_amount) || 0;
		const slYearCount = Number(lastSlRow.year_count) || 1;

		const lastSlCnRow = lastSlCnResult[0] || {};
		const lastSlCnAmount = Number(lastSlCnRow.cn_amount) || 0;

		const arRow = arResult[0] || {};
		const arIvAmount = Number(arRow.iv_amount) || 0;
		const arCnAmount = Number(arRow.cn_amount) || 0;
		const arDnAmount = Number(arRow.dn_amount) || 0;

		const lastArRow = lastArResult[0] || {};
		const lastArIvAmount = Number(lastArRow.iv_amount) || 0;
		const lastArCnAmount = Number(lastArRow.cn_amount) || 0;
		const lastArDnAmount = Number(lastArRow.dn_amount) || 0;
		const arYearCount = Number(lastArRow.year_count) || 1;

		let salesAmount: number;
		let lastAsOfSales: number;

		if (this.slPhModuleOnly) {
			salesAmount = slIvCsAmount
				- (this.includeCreditNote ? slCnAmount : 0)
				+ (this.includeDebitNote ? slDnAmount : 0);
			lastAsOfSales = lastSlIvCsAmount
				- (this.includeCreditNote ? lastSlCnAmount : 0)
				+ (this.includeDebitNote ? lastSlDnAmount : 0);
			this.servingYear = slYearCount;
		} else {
			salesAmount = arIvAmount
				- (this.includeCreditNote ? arCnAmount : 0)
				+ (this.includeDebitNote ? arDnAmount : 0);
			lastAsOfSales = lastArIvAmount
				- (this.includeCreditNote ? lastArCnAmount : 0)
				+ (this.includeDebitNote ? lastArDnAmount : 0);
			this.servingYear = arYearCount;
		}

		this.allTimeSales = salesAmount;

		this.averageAnnualSales = roundToFixed(
			lastAsOfSales / this.servingYear,
			this.decimal,
		);

		this.generateLoadingText(1);
		this.saveHistory(
			'customersCount',
			'allTimeSales',
			'averageAnnualSales',
			'servingYear',
			'accountManager',
			'relationship',
		);
	}
}
